import { useEffect, useRef } from 'react';
import { Quote } from 'lucide-react';

const testimonials = [
  {
    name: 'Kathy and Sean L.',
    role: 'Business Owners',
    text: 'The structuring strategies provided by Global Citizen Consulting have given us a profound sense of security. Knowing that our wealth and legacy are safeguarded against unforeseen circumstances gives us the confidence to plan with a longer horizon. Their tailored approach means our assets are protected and our family\'s future is architected, not left to chance.',
    image: 'https://images.squarespace-cdn.com/content/v1/657661ff81e07c061188ea83/1702329126202-EXP7NV8ZQZ0159CFZ7J1/image-asset.jpeg',
  },
  {
    name: 'Stephen C.',
    role: 'Senior Executive',
    text: 'Being able to plan the jurisdictional architecture for my family was a significant source of complexity. Global Citizen Consulting helped me focus on what matters: a coherent strategy that works across borders, not just a single transaction.',
    image: 'https://images.squarespace-cdn.com/content/v1/657661ff81e07c061188ea83/1702329362532-F2VRTIFXE18N2S0V003Z/image-asset.jpeg',
  },
  {
    name: 'Marisa S.',
    role: 'Consultant',
    text: 'Before retaining Global Citizen Consulting, I felt overwhelmed by the complexities of cross border structuring. Their team not only simplified the process but also identified strategies I had not considered, allowing me to reinvest in my growing business with clarity. It is not just advisory. It is a different way of thinking about where you stand in the world.',
    image: 'https://images.squarespace-cdn.com/content/v1/657661ff81e07c061188ea83/1702328995759-THWX6SOY39CDQX6WQ2I2/image-asset.jpeg',
  },
];

export default function Testimonials() {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) entry.target.classList.add('visible');
        });
      },
      { threshold: 0.1 }
    );
    const els = sectionRef.current?.querySelectorAll('.reveal');
    els?.forEach((el) => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="testimonials"
      ref={sectionRef}
      data-testid="testimonials-section"
      className="relative py-24 md:py-32 overflow-hidden bg-[#f7f6f3]"
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="text-center mb-16 reveal">
          <p
            className="text-sm uppercase tracking-[0.3em] text-[#1a7a7a] mb-4 font-medium"
            style={{ fontFamily: "'Montserrat', sans-serif" }}
          >
            Client Perspectives
          </p>
          <h2
            className="text-4xl md:text-5xl font-semibold tracking-tight text-[#1a1a1a]"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            What Our Clients Say
          </h2>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {testimonials.map((t, idx) => (
            <div
              key={t.name}
              data-testid={`testimonial-card-${idx}`}
              className={`reveal stagger-${idx + 1} group relative rounded-2xl bg-white border border-gray-200 p-8 hover:border-[#1a7a7a]/30 hover:shadow-lg transition-all duration-500 flex flex-col`}
            >
              <Quote className="text-[#1a7a7a]/20 mb-4" size={36} />

              <p
                className="text-gray-600 leading-relaxed mb-8 flex-1 text-sm md:text-base"
                style={{ fontFamily: "'Montserrat', sans-serif" }}
              >
                "{t.text}"
              </p>

              <div className="flex items-center gap-4 mt-auto pt-6 border-t border-gray-100">
                <img
                  src={t.image}
                  alt={t.name}
                  className="w-12 h-12 rounded-full object-cover border-2 border-[#1a7a7a]/20"
                />
                <div>
                  <p
                    className="text-[#1a1a1a] font-semibold text-sm"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {t.name}
                  </p>
                  <p
                    className="text-[#1a7a7a] text-xs"
                    style={{ fontFamily: "'Montserrat', sans-serif" }}
                  >
                    {t.role}
                  </p>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
